import { Flex, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type { ExchangeRateFragment } from "@src/__generated__/urql-graphql";
import type { CurrencyModel } from "@src/components/modules/clients/detail/models";
import { CurrencyFlagIcon } from "@src/components/ui-kit";
import { DataTableActionButton } from "@src/components/ui-kit/DataTable/DataTableActionButton";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModalExchangeRate } from "@src/components/widgets/Modals/ModalExchangeRate";
import { formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent } from "react";
import { useToggle } from "react-use";

const CurrencyCollapsibleRows: FunctionComponent<
  React.PropsWithChildren<{
    model: CurrencyModel;
    source: "client" | "workspace";
    onEdit?: (rate: ExchangeRateFragment) => void;
  }>
> = observer(function CurrencyCollapsibleRows({ model, source, onEdit }) {
  const code = model.currency.code;
  const rates = model.rates;
  const multipleRates = rates.length > 1;

  const { UIStore } = useStore();
  const [open, toggle] = useToggle(multipleRates);

  const rows = rates.map((j, idx) => {
    return (
      <tr
        key={j.id}
        className="Polaris-DataTable__Row"
        style={{
          display: idx === 0 ? "table-row" : open ? "table-row" : "none",
        }}
      >
        <td className="Polaris-DataTable__Cell">
          {idx === 0 && (
            <Flex
              textColor="#3A7995"
              cursor={multipleRates ? "pointer" : undefined}
              onClick={() => multipleRates && toggle()}
            >
              {multipleRates && (
                <Icon
                  name={open ? "chevron-down" : "chevron-right"}
                  color="grey.500"
                  mr="2"
                />
              )}
              <CurrencyFlagIcon className="mr-4" code={code} /> {code}
            </Flex>
          )}
        </td>
        <td className="Polaris-DataTable__Cell">{j.rate.value.toFixed(2)}</td>
        <td className="Polaris-DataTable__Cell">{formatDate(j.valid_from)}</td>
        <td className="Polaris-DataTable__Cell">{formatDate(j.valid_to)}</td>
        <td className="Polaris-DataTable__Cell">
          <Menu>
            <MenuButton type="button">
              <DataTableActionButton aria-label={t`actions`} />
            </MenuButton>

            <MenuList>
              <MenuItem
                onClick={() => {
                  UIStore.dialogs.openModal({
                    content: (
                      <ModalExchangeRate
                        source={source}
                        title={t`Edit rate`}
                        exchangeRateId={j.id}
                        valid_from={j.valid_from}
                        valid_to={j.valid_to}
                        exchange_rate={j.rate.denominatedValue}
                        onSubmit={(rate) => onEdit?.(rate)}
                        exchangeCurrencyId={j.currency.id}
                      />
                    ),
                  });
                }}
              >
                <Trans>Edit</Trans>
              </MenuItem>
            </MenuList>
          </Menu>
        </td>
      </tr>
    );
  });
  return <React.Fragment>{rows}</React.Fragment>;
});

type ExchangeRatesTableProps = {
  ratesGroupedByCurrency: CurrencyModel[];
  source: "client" | "workspace";
  onEdit?: (rate: ExchangeRateFragment) => void;
};

export const ExchangeRatesTable: FunctionComponent<ExchangeRatesTableProps> = ({
  ratesGroupedByCurrency,
  source,
  onEdit,
}) => {
  return (
    <table className="Polaris-DataTable__Table">
      <thead>
        <tr className="Polaris-DataTable__Row">
          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--total">
            <Trans>Currency</Trans>
          </td>
          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--total">
            <Trans>Rate</Trans>
          </td>
          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--total">
            <Trans>Valid from</Trans>
          </td>
          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--total">
            <Trans>Valid to</Trans>
          </td>
          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--total"></td>
        </tr>
      </thead>
      <tbody>
        {ratesGroupedByCurrency.map((i) => {
          return (
            <CurrencyCollapsibleRows
              onEdit={onEdit}
              source={source}
              key={i.currency.id}
              model={i}
            />
          );
        })}
      </tbody>
    </table>
  );
};
